import { useTranslation } from 'react-i18next';
import { Archive, AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export function ArchiveConfirmDialog({ open, onOpenChange, onConfirm, loading }) {
  const { t } = useTranslation(['common', 'my-docs']);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-black">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <Archive className="w-5 h-5 text-muted-foreground" />
            {t('my-docs:archive.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t('my-docs:archive.confirm')}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={onConfirm} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('my-docs:archive.action')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Bulk delete confirmation. Signed E-IMZO docs in the selection can't be
// deleted — the dialog surfaces that up-front and the single primary action
// archives them while deleting everything else (the user opted for this combined
// flow over a silent post-hoc toast).
export function BulkDeleteDialog({ open, onOpenChange, deletableCount, signedCount, onConfirm, loading }) {
  const { t } = useTranslation(['common', 'my-docs']);
  const hasSigned = signedCount > 0;
  let actionLabel;
  if (!hasSigned) {
    actionLabel = t('my-docs:btn.delete');
  } else if (deletableCount > 0) {
    actionLabel = t('my-docs:bulk.archiveAndDelete');
  } else {
    actionLabel = t('my-docs:bulk.archiveSigned', { count: signedCount });
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-black">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            {t('my-docs:bulk.confirmTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {deletableCount > 0 && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('my-docs:bulk.confirmDelete', { count: deletableCount })}
            </p>
          )}
          {hasSigned && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-700 flex items-start gap-2">
                <Archive className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {t('my-docs:bulk.signedArchiveNotice', { count: signedCount })}
                </span>
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:cancel')}
            </Button>
            <Button variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {actionLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Permanent (hard) delete from the trash — irreversible, no archive fallback.
// Used only inside the Trash location; the copy leans hard on "can't be undone".
export function PermanentDeleteDialog({ open, onOpenChange, count, onConfirm, loading }) {
  const { t } = useTranslation(['common', 'my-docs']);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-black">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            {t('my-docs:trash.purgeTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t('my-docs:trash.purgeConfirm', { count })}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:cancel')}
            </Button>
            <Button variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              {t('my-docs:trash.purgeAction')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RestoreConfirmDialog({ open, onOpenChange, onConfirm, loading, needsFolderPick }) {
  const { t } = useTranslation(['common', 'my-docs']);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-black">
        <DialogHeader>
          <DialogTitle>{t('my-docs:restore.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {needsFolderPick
              ? t('my-docs:restore.folderGoneHint')
              : t('my-docs:restore.confirm')}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={onConfirm} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('my-docs:restore.action')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
